import { useMutation } from "@apollo/client";
import classNames from "classnames";
import { useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { MentionableTextField, referencesFromMentionText } from "@/components/AtMention";
import Button from "@/components/Button";
import Chip, { ChipSet } from "@/components/Chip/Chip";
import Dialog from "@/components/Dialog";
import IconButton from "@/components/IconButton";
import { PGAAssistantIcon } from "@/components/CustomSVGIcons/PGAAssistantIcon";
import {
  CREATE_TASK_MUTATION,
  type BackofficeCRMCreateTaskInput,
  type CreateTaskResponse
} from "@/features/global/components/AddTaskSideSheet/mutations";
import {
  DEFAULT_QUICK_DUE_DATE_DAYS,
  QUICK_DUE_DATE_OPTIONS,
  isoDateDaysFromNow,
  quickDueDateChipLabel
} from "@/features/global/components/AddTaskSideSheet/quickDueDate";
import useAmplitude from "@/hooks/useAmplitude";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import { useIsMobile } from "@/hooks/useIsMobile";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { TASKS_QUERY } from "@/pages/tasks/queries";
import { TASK_ANALYTICS_EVENTS, getCreatedTaskAnalyticsData } from "@/pages/tasks/_utils/amplitude";
import { TaskStageValues, TaskVisibilityValues } from "@/types";
import useQuickAddMentionSearch from "./useQuickAddMentionSearch";

const TASK_TAB_ID = "dashboard-quick-add-tab-task";
const ASK_TAB_ID = "dashboard-quick-add-tab-ask";
export const ASK_FAKE_DOOR_ANALYTICS_EVENT = "clicked AI ask fake door";
export const ASK_COMING_SOON_HEADLINE_REST = "may be coming soon...";
export const ASK_COMING_SOON_BODY =
  "Thanks for sharing your interest in an AI chat feature. We'll let you know when it's ready!";
export const ASK_COMING_SOON_DISMISS_LABEL = "Got it";

const ASK_TAB_ICON_FILL = "rgb(var(--color-sys-brand-on-surface-variant))";
const ASK_HEADLINE_ICON_FILL = "rgb(var(--color-sys-brand-primary))";

function AskComingSoonHeadline() {
  return (
    <span>
      <span className="whitespace-nowrap text-sys-brand-primary">
        <PGAAssistantIcon
          size={20}
          fill={ASK_HEADLINE_ICON_FILL}
          className="mr-1 inline-block size-[1.25em] align-[-0.15em]"
          data-testid="dashboard-quick-add-ask-coming-soon-icon"
        />
        Ask
      </span>
      <span className="ml-0.5">{` ${ASK_COMING_SOON_HEADLINE_REST}`}</span>
    </span>
  );
}

function composerChromeClassName(selected: boolean) {
  return classNames(
    "relative m-0 inline-flex items-center gap-1.5 rounded-t-lg border px-4 py-2.5 text-label-large",
    selected
      ? "-mb-px z-[1] border-sys-brand-outline-variant border-b-sys-brand-surface bg-sys-brand-surface text-sys-brand-on-surface"
      : "z-0 border-transparent bg-sys-brand-surface-container-high text-sys-brand-on-surface-variant"
  );
}

function TaskComposerTab() {
  return (
    <span data-testid={TASK_TAB_ID} className={composerChromeClassName(true)}>
      Task
    </span>
  );
}

function AskFakeDoor({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      data-testid={ASK_TAB_ID}
      aria-label="Ask (coming soon)"
      onClick={onClick}
      className={classNames(composerChromeClassName(false), "cursor-pointer")}
    >
      <PGAAssistantIcon size={16} fill={ASK_TAB_ICON_FILL} className="h-4 w-4 shrink-0 overflow-visible" />
      Ask
    </button>
  );
}

export default function QuickAddTask() {
  const { trackEvent } = useAmplitude();
  const currentUser = useCurrentBackofficeUser();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const submittingRef = useRef(false);

  const [title, setTitle] = useState("");
  const [selectedDays, setSelectedDays] = useState<number>(DEFAULT_QUICK_DUE_DATE_DAYS);
  const [nameError, setNameError] = useState<string | undefined>();
  const [askComingSoonOpen, setAskComingSoonOpen] = useState(false);

  const [createTask, { loading: createLoading }] = useMutation<CreateTaskResponse>(CREATE_TASK_MUTATION, {
    refetchQueries: [TASKS_QUERY]
  });

  const trimmedTitle = title.trim();
  const canSubmit = Boolean(trimmedTitle && currentUser?.id && !createLoading);

  async function submitTask() {
    if (createLoading || submittingRef.current) return;

    if (!trimmedTitle) {
      setNameError("Task name is required");
      return;
    }

    if (!currentUser?.id) {
      showErrorSnackbar("You must be signed in to add a task.");
      return;
    }

    const dueDate = isoDateDaysFromNow(selectedDays);

    const input: BackofficeCRMCreateTaskInput = {
      name: trimmedTitle,
      description: "",
      assignedToId: currentUser.id,
      visibility: TaskVisibilityValues.PRIVATE
    };
    const references = referencesFromMentionText(trimmedTitle);
    if (references.length > 0) {
      input.references = references;
    }

    if (dueDate) {
      input.dueDate = dueDate;
    }

    submittingRef.current = true;

    try {
      const result = await createTask({ variables: { input } });

      if (result.data?.createTask?.success) {
        const taskId = result.data.createTask.task?.id;
        trackEvent(
          TASK_ANALYTICS_EVENTS.created,
          getCreatedTaskAnalyticsData({
            taskId,
            source: "dashboard_quick_add",
            stage: TaskStageValues.OPEN,
            visibility: TaskVisibilityValues.PRIVATE,
            dueDate,
            referenceTypes: references.map((reference) => reference.referenceableType),
            hasAssignee: true,
            teamTagCount: 0
          })
        );
        showSnackbar("Task created", {
          actions: taskId
            ? [
                <Button key="view" variant="text" onClick={() => navigate(`/tasks/${taskId}`)}>
                  View Task
                </Button>
              ]
            : undefined
        });
        setTitle("");
        setSelectedDays(DEFAULT_QUICK_DUE_DATE_DAYS);
        setNameError(undefined);
        return;
      }

      showErrorSnackbar(result.data?.createTask?.message || "Failed to create task");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Error creating task. Please try again.";
      showErrorSnackbar(errorMessage);
    } finally {
      submittingRef.current = false;
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submitTask();
  }

  function handleComposerEnter() {
    if (canSubmit) {
      formRef.current?.requestSubmit();
    }
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-3" data-testid="dashboard-quick-add-task">
      <form ref={formRef} className="flex w-full min-w-0 flex-col gap-3" onSubmit={handleSubmit}>
        <div
          className="rounded-lg border border-extended-pgadarknavy-brand-color bg-sys-brand-surface"
          data-testid="dashboard-quick-add-card"
        >
          <div
            data-testid="dashboard-quick-add-tablist"
            className="flex items-end gap-0 overflow-hidden rounded-t-lg border-b border-sys-brand-outline-variant bg-sys-brand-surface-container-high px-3 pt-1"
          >
            <TaskComposerTab />
            <AskFakeDoor
              onClick={() => {
                trackEvent(ASK_FAKE_DOOR_ANALYTICS_EVENT);
                setAskComingSoonOpen(true);
              }}
            />
          </div>
          <div className="relative z-10 flex flex-col gap-2 p-3">
            <MentionableTextField
              compact
              className="w-full min-w-0"
              overlayClassName="min-w-0 w-full"
              disabled={createLoading || !currentUser?.id}
              error={!!nameError}
              errorText={nameError}
              placeholder="Add a new task..."
              useSearch={useQuickAddMentionSearch}
              allowSpacesInQuery
              value={title}
              onChange={(value) => {
                setTitle(value);
                if (nameError) setNameError(undefined);
              }}
              onEnter={handleComposerEnter}
            />
          </div>
          <div
            className="flex w-full min-w-0 flex-nowrap items-center gap-3 rounded-b-lg border-t border-sys-brand-outline-variant bg-sys-brand-surface-container-low px-3 py-2"
            data-testid="dashboard-quick-add-footer"
          >
            <div
              className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-visible"
              data-testid="dashboard-quick-add-due-row"
            >
              <p id="quick-add-task-due-label" className="shrink-0 text-label-medium text-sys-brand-on-surface-variant">
                Due
              </p>
              <div className="min-w-0 flex-1 overflow-visible [&_md-chip-set]:flex-nowrap [&_md-chip-set]:overflow-visible">
                <ChipSet aria-labelledby="quick-add-task-due-label">
                  {QUICK_DUE_DATE_OPTIONS.map((option) => (
                    <Chip
                      key={option.days}
                      color="primary"
                      label={quickDueDateChipLabel(option, isMobile)}
                      showCheckmark={false}
                      selected={selectedDays === option.days}
                      disabled={createLoading || !currentUser?.id}
                      onClick={() => setSelectedDays(option.days)}
                    />
                  ))}
                </ChipSet>
              </div>
            </div>
            <IconButton
              name="check"
              variant="filled"
              skipMargin
              containerClassName="ml-1 shrink-0"
              title="Save"
              ariaLabel="Save"
              disabled={!canSubmit}
              data-testid="dashboard-quick-add-task-submit"
              type="button"
              onClick={() => {
                if (canSubmit) {
                  formRef.current?.requestSubmit();
                }
              }}
            />
          </div>
        </div>
      </form>
      <Dialog
        open={askComingSoonOpen}
        onClosed={() => setAskComingSoonOpen(false)}
        headline={<AskComingSoonHeadline />}
        actions={
          <Button
            variant="filled"
            testId="dashboard-quick-add-ask-coming-soon-dismiss"
            onClick={() => setAskComingSoonOpen(false)}
          >
            {ASK_COMING_SOON_DISMISS_LABEL}
          </Button>
        }
      >
        <p className="text-body-medium text-sys-brand-on-surface">{ASK_COMING_SOON_BODY}</p>
      </Dialog>
    </div>
  );
}
